import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { mobile } from "../../ui/media";
import { Button } from "../../ui/Button";
import { useAuth } from "../../context/AuthContext";

const STORAGE_KEY = "dora_welcome_seen";
const SHOW_DELAY = 4000;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.overlay};
  background: ${({ theme }) => theme.colors.state.scrim};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(2)};
  animation: ${fadeIn} ${({ theme }) => theme.motion.base} ease;
`;

const Modal = styled(motion.div)`
  position: relative;
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${({ theme }) => theme.radius.xl}px;
  box-shadow: ${({ theme }) => theme.colors.shadow.high};
  padding: ${({ theme }) => theme.spacing(5)};
  max-width: 440px;
  width: 100%;
  text-align: center;

  ${mobile} {
    padding: ${({ theme }) => theme.spacing(3)};
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1.5)};
  right: ${({ theme }) => theme.spacing(1.5)};
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill}px;
  background: ${({ theme }) => theme.colors.state.hover};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    background: ${({ theme }) => theme.colors.state.active};
  }
`;

const Emoji = styled.div`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.typography.h2};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Desc = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

const Code = styled.div`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2.5)};
  background: ${({ theme }) => theme.colors.state.selected};
  border: 2px dashed ${({ theme }) => theme.colors.accent.primary};
  border-radius: ${({ theme }) => theme.radius.md}px;
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.h3};
  color: ${({ theme }) => theme.colors.accent.primary};
  letter-spacing: 2px;
  margin: ${({ theme }) => theme.spacing(2)} 0;
`;

const Fine = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.disabled};
`;

const WelcomeDiscount = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!token) return;

    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }

    const timer = setTimeout(() => {
      setVisible(true);
    }, SHOW_DELAY);
    return () => clearTimeout(timer);
  }, [token]);

  const close = () => {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
  };

  return (
    <AnimatePresence>
      {visible ? (
        <Backdrop
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <Modal
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseBtn onClick={close} aria-label="Close">
              {"\u2715"}
            </CloseBtn>
            <Emoji aria-hidden="true">{"\uD83C\uDF3E"}</Emoji>
            <Title>{t("welcome.title")}</Title>
            <Desc>{t("welcome.description")}</Desc>
            <Code>MIRSEERDHE10</Code>
            <Fine>{t("welcome.fine_print")}</Fine>
            <Button variant="primary" size="lg" fullWidth onClick={close}>
              {t("welcome.cta")}
            </Button>
          </Modal>
        </Backdrop>
      ) : null}
    </AnimatePresence>
  );
};

export default WelcomeDiscount;
